/**
 * Vererbungs-Logik für den Content-Block-basierten Channels-Editor (C2).
 *
 * Ein Channel „erbt" Felder vom Produkt (Master); existiert für einen Feldtyp
 * ein Channel-Block, ist das eine Abweichung (Override). Diese Helfer mergen
 * Master- + Channel-Blocks zu einer Feldliste und zählen die Abweichungen.
 */

import type { ContentBlock, BlockType, ScopeType } from '@/types/content-blocks'

const BLOCK_ORDER: BlockType[] = ['title', 'short_description', 'description', 'bullets', 'seo_meta']

/**
 * Text-Standardfelder, die im Editor IMMER sichtbar sein sollen — auch wenn
 * (noch) kein Content-Block existiert. seo_meta (zusammengesetzt) und bullets
 * (eigenes Panel) sind bewusst ausgenommen: ein leeres Plain-Text-Feld wäre
 * für sie falsch.
 */
export const ALWAYS_SHOWN_FIELD_TYPES: BlockType[] = ['title', 'short_description', 'description']

export interface MergedField {
  blockType: BlockType
  block: ContentBlock
  /** true = vom Master geerbt (kein Channel-Override) */
  inherited: boolean
}

/**
 * Synthetischer, leerer Platzhalter-Block für einen noch nicht existierenden
 * Feld-Typ. Erkennbar an `id === ''` — der Save-Pfad legt ihn beim ersten
 * Speichern an (statt zu updaten).
 */
export function makePlaceholderBlock(
  blockType: BlockType,
  scopeType: ScopeType,
  scopeKey: string,
): ContentBlock {
  return {
    id: '',
    workspace_id: '',
    scope_type: scopeType,
    scope_key: scopeKey,
    block_type: blockType,
    current_version_id: null,
    created_at: '',
    updated_at: '',
    current_version: undefined,
  }
}

/**
 * Stellt sicher, dass alle Text-Standardfelder in der Feldliste vorhanden sind.
 * Fehlende Typen werden als leerer Platzhalter ergänzt, das Ergebnis bleibt in
 * fachlicher Feld-Reihenfolge (BLOCK_ORDER). Existierende Felder (auch geerbte)
 * bleiben unverändert.
 */
export function withAlwaysShownFields(
  fields: MergedField[],
  scopeType: ScopeType,
  scopeKey: string,
): MergedField[] {
  const present = new Set(fields.map((f) => f.blockType))
  const placeholders = ALWAYS_SHOWN_FIELD_TYPES.filter((bt) => !present.has(bt)).map(
    (bt) => ({
      blockType: bt,
      block: makePlaceholderBlock(bt, scopeType, scopeKey),
      inherited: false,
    }),
  )
  return [...fields, ...placeholders].sort(
    (a, b) => BLOCK_ORDER.indexOf(a.blockType) - BLOCK_ORDER.indexOf(b.blockType),
  )
}

function byType(blocks: ContentBlock[]): Map<BlockType, ContentBlock> {
  return new Map(blocks.map((b) => [b.block_type, b]))
}

/**
 * Mergt Master- + Channel-Blocks zur Feldliste: Channel-Block = Abweichung,
 * sonst Master-Block = geerbt. Sortiert nach fachlicher Feld-Reihenfolge.
 */
export function mergeFields(master: ContentBlock[], channel: ContentBlock[]): MergedField[] {
  const masterMap = byType(master)
  const channelMap = byType(channel)
  const types = new Set<BlockType>([...masterMap.keys(), ...channelMap.keys()])
  const fields: MergedField[] = []
  for (const type of types) {
    const channelBlock = channelMap.get(type)
    if (channelBlock) {
      fields.push({ blockType: type, block: channelBlock, inherited: false })
    } else {
      const masterBlock = masterMap.get(type)
      if (masterBlock) fields.push({ blockType: type, block: masterBlock, inherited: true })
    }
  }
  return fields.sort((a, b) => BLOCK_ORDER.indexOf(a.blockType) - BLOCK_ORDER.indexOf(b.blockType))
}

/** Anzahl Abweichungen eines Channels gegenüber dem Master (= vorhandene Channel-Blocks). */
export function overrideCount(channel: ContentBlock[]): number {
  return channel.length
}
